"use client";
import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useContactModal } from '@/contexts/ContactModalContext';

const IconUnderstand = ({ color }: { color: string }) => (
    <svg width="30" height="30" viewBox="0 0 30 30" fill="none">
        <circle cx="13" cy="13" r="8.5" stroke={color} strokeWidth="2.2" />
        <path d="M19 19l6 6" stroke={color} strokeWidth="2.2" strokeLinecap="round" />
    </svg>
);
const IconStrategise = ({ color }: { color: string }) => (
    <svg width="30" height="30" viewBox="0 0 30 30" fill="none">
        <circle cx="15" cy="6" r="3" stroke={color} strokeWidth="2.2" />
        <circle cx="6" cy="24" r="3" stroke={color} strokeWidth="2.2" />
        <circle cx="24" cy="24" r="3" stroke={color} strokeWidth="2.2" />
        <path d="M15 9v6M15 15l-9 6M15 15l9 6" stroke={color} strokeWidth="2.2" strokeLinecap="round" />
    </svg>
);
const IconLaunch = ({ color }: { color: string }) => (
    <svg width="30" height="30" viewBox="0 0 30 30" fill="none">
        <path d="M17 3L6 17.5h6.5L11 27l11-14.5h-6.5L17 3z" fill={color} />
    </svg>
);
const IconTest = ({ color }: { color: string }) => (
    <svg width="30" height="30" viewBox="0 0 30 30" fill="none">
        <rect x="4" y="4" width="9" height="18" rx="2" stroke={color} strokeWidth="2.2" />
        <rect x="17" y="4" width="9" height="18" rx="2" stroke={color} strokeWidth="2.2" />
        <path d="M8.5 9v4M21.5 12v4" stroke={color} strokeWidth="2.2" strokeLinecap="round" />
    </svg>
);
const IconOptimise = ({ color }: { color: string }) => (
    <svg width="30" height="30" viewBox="0 0 30 30" fill="none">
        <circle cx="15" cy="15" r="4" stroke={color} strokeWidth="2.2" />
        <path d="M15 2v4M15 24v4M2 15h4M24 15h4M6.5 6.5l2.8 2.8M20.7 20.7l2.8 2.8M23.5 6.5l-2.8 2.8M9.3 20.7l-2.8 2.8" stroke={color} strokeWidth="2" strokeLinecap="round" />
    </svg>
);
const IconScale = ({ color }: { color: string }) => (
    <svg width="30" height="30" viewBox="0 0 30 30" fill="none">
        <path d="M4 26L12 14l6 6 8-14" stroke={color} strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M20 6h6v6" stroke={color} strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const steps = [
    { num: '01', title: 'Understand', desc: 'Audience, product, positioning and business objectives.', Icon: IconUnderstand },
    { num: '02', title: 'Strategise', desc: 'Campaign structure, audience segments, creative angles and offers.', Icon: IconStrategise },
    { num: '03', title: 'Launch', desc: 'Campaigns, tracking, audiences and creative variations.', Icon: IconLaunch },
    { num: '04', title: 'Test', desc: 'Hooks, formats, audiences, messages and landing experiences.', Icon: IconTest },
    { num: '05', title: 'Optimise', desc: 'Shift budget toward combinations that produce stronger outcomes.', Icon: IconOptimise },
    { num: '06', title: 'Scale', desc: 'Expand winning audiences and creative while monitoring efficiency.', Icon: IconScale },
];

const AUTOPLAY_MS = 4000;

export default function MetaAdsProcess() {
    const { openModal } = useContactModal();
    const [active, setActive] = useState(0);
    const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

    useEffect(() => {
        timerRef.current = setInterval(() => {
            setActive((prev) => (prev + 1) % steps.length);
        }, AUTOPLAY_MS);
        return () => { if (timerRef.current) clearInterval(timerRef.current); };
    }, [active]);

    const select = (i: number) => {
        if (timerRef.current) clearInterval(timerRef.current);
        setActive(i);
    };

    const cur = steps[active];

    return (
        <section className="relative overflow-hidden py-16 md:py-28 px-4 md:px-16 lg:px-24" style={{ background: '#0f0f0f' }}>
            <div className="absolute inset-0 pointer-events-none opacity-[0.05]" style={{ backgroundImage: 'radial-gradient(circle, rgba(206,255,26,1) 1px, transparent 1px)', backgroundSize: '40px 40px' }} />

            <div className="max-w-[1000px] mx-auto flex flex-col gap-14 md:gap-16 relative z-10">

                {/* Header */}
                <div className="flex flex-col items-center gap-4 text-center">
                    <motion.span initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }}
                        className="text-xs font-bold tracking-[0.25em] uppercase"
                        style={{ color: 'rgba(206,255,26,0.85)', fontFamily: 'var(--font-clash), "Clash Display", sans-serif' }}>
                        How We Work
                    </motion.span>
                    <motion.h2 initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7, delay: 0.1 }}
                        className="text-2xl md:text-6xl font-bold tracking-tight leading-tight"
                        style={{ fontFamily: 'var(--font-clash), "Clash Display", sans-serif', color: '#ffffff' }}>
                        Our <span style={{ color: '#ceff1a' }}>Meta Ads Process</span>
                    </motion.h2>
                </div>

                {/* Story-style segmented progress bar */}
                <div className="flex items-center gap-1.5 max-w-xl mx-auto w-full">
                    {steps.map((step, i) => (
                        <button
                            key={i}
                            onClick={() => select(i)}
                            aria-label={`Jump to ${step.title}`}
                            className="relative flex-1 h-1.5 rounded-full overflow-hidden"
                            style={{ background: 'rgba(255,255,255,0.12)', border: 'none', cursor: 'pointer', padding: 0 }}
                        >
                            {i < active && <div className="absolute inset-0" style={{ background: '#ceff1a' }} />}
                            {i === active && (
                                <motion.div
                                    key={active}
                                    initial={{ width: '0%' }}
                                    animate={{ width: '100%' }}
                                    transition={{ duration: AUTOPLAY_MS / 1000, ease: 'linear' }}
                                    className="absolute inset-y-0 left-0"
                                    style={{ background: '#ceff1a' }}
                                />
                            )}
                        </button>
                    ))}
                </div>

                {/* Active step "story" card */}
                <div className="relative max-w-xl mx-auto w-full" style={{ minHeight: 240 }}>
                    <AnimatePresence mode="wait">
                        <motion.div
                            key={active}
                            initial={{ opacity: 0, y: 16 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -16 }}
                            transition={{ duration: 0.4, ease: [0.25, 0.46, 0.45, 0.94] }}
                            className="relative rounded-[28px] p-8 md:p-10 flex flex-col gap-6 overflow-hidden"
                            style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.15)' }}
                        >
                            <div className="flex items-center gap-3">
                                <span
                                    className="w-11 h-11 rounded-full flex items-center justify-center shrink-0"
                                    style={{ background: '#ceff1a' }}
                                >
                                    <cur.Icon color="#080400" />
                                </span>
                                <div className="flex flex-col leading-tight">
                                    <span className="text-xs font-bold uppercase tracking-wider" style={{ fontFamily: 'var(--font-clash), "Clash Display", sans-serif', color: '#ffffff' }}>
                                        Meta Ads Process
                                    </span>
                                    <span className="text-[11px] font-mono" style={{ color: 'rgba(255,255,255,0.4)' }}>
                                        Step {cur.num} of {String(steps.length).padStart(2, '0')}
                                    </span>
                                </div>
                            </div>

                            <div className="flex flex-col gap-3">
                                <h3 className="text-2xl md:text-4xl font-bold" style={{ fontFamily: 'var(--font-clash), "Clash Display", sans-serif', color: '#ffffff', letterSpacing: '0.01em' }}>
                                    {cur.title}
                                </h3>
                                <p className="text-sm md:text-lg leading-relaxed" style={{ fontFamily: 'var(--font-manrope), Manrope, sans-serif', color: 'rgba(255,255,255,0.6)' }}>
                                    {cur.desc}
                                </p>
                            </div>
                        </motion.div>
                    </AnimatePresence>
                </div>

                {/* CTA */}
                <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }} className="flex justify-center">
                    <button onClick={openModal} className="inline-flex items-center justify-center whitespace-nowrap px-6 md:px-10 py-4 md:py-5 rounded-full font-semibold text-sm sm:text-base md:text-lg transition-all duration-300 hover:scale-105"
                        style={{ background: '#ceff1a', color: '#080400', fontFamily: 'var(--font-clash), "Clash Display", sans-serif', letterSpacing: '0.08em' }}>
                        Build My Meta Ads Strategy →
                    </button>
                </motion.div>
            </div>
        </section>
    );
}
